<script setup>
import { reactive, computed, watch, getCurrentInstance, ref } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';
import { TRANSACTIONS } from '/src/app/features/transaction/constants.js';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

// DATA SOURCES
const contactsSource = computed(() => $stores.appData?.state?.data?.contacts || {});
const addressesSource = computed(() => $stores.appData?.state?.data?.addresses || {});
const serviceAvailabilitySource = computed(() => $stores.appData?.state?.data?.serviceAvailability || {});
const specificTutorialsSource = computed(() => $stores.appData?.state?.data?.general?.specificTutorials || {});

// ALL UNIQUE COUNTRY CODES
const countryCodes = computed(() => {
  const codes = new Set([
    ...Object.keys(contactsSource.value).filter(k => contactsSource.value[k]),
    ...Object.keys(addressesSource.value).filter(k => addressesSource.value[k])
  ]);
  return Array.from(codes);
});

const selectedCountry = ref(countryCodes.value[0] || '');

// FORMS
const forms = reactive({
  contacts: {},
  addresses: {},
  serviceAvailability: {},
  specificTutorials: {}
});

// INITIALIZATION
watch(
  [contactsSource, addressesSource, serviceAvailabilitySource, specificTutorialsSource],
  ([contacts, addresses, availability, specificTutorials]) => {
    countryCodes.value.forEach((code) => {
      // Initialize contacts for this country
      if (!forms.contacts[code]) {
        forms.contacts[code] = {};
      }
      Object.assign(forms.contacts[code], {
        whatsappPhoneNumber: contacts?.[code]?.whatsappPhoneNumber || '',
        whatsappPhoneNumberComplaint: contacts?.[code]?.whatsappPhoneNumberComplaint || '',
        whatsappLink: contacts?.[code]?.whatsappLink || '',
        whatsappChatLink: contacts?.[code]?.whatsappChatLink || '',
        telegramLink: contacts?.[code]?.telegramLink || ''
      });

      // Initialize addresses for this country
      if (!forms.addresses[code]) {
        forms.addresses[code] = {};
      }
      Object.assign(forms.addresses[code], {
        usdtTrc20Address: addresses?.[code]?.usdtTrc20Address || '',
        usdtErc20Address: addresses?.[code]?.usdtErc20Address || '',
        usdtBep20Address: addresses?.[code]?.usdtBep20Address || '',
        binanceAccountId: addresses?.[code]?.binanceAccountId || '',
        betwinnerWithdrawAddress: addresses?.[code]?.betwinnerWithdrawAddress || '',
        melbetWithdrawAddress: addresses?.[code]?.melbetWithdrawAddress || '',
        eightStarzWithdrawAddress: addresses?.[code]?.eightStarzWithdrawAddress || '',
        oneWinWithdrawAddress: addresses?.[code]?.oneWinWithdrawAddress || '',
        xbetWithdrawAddress: addresses?.[code]?.xbetWithdrawAddress || ''
      });

      // Initialize service availability for this country
      const opKeys = [
        'fund1xBet', 'withdrawal1xBet',
        'fundBetWinner', 'withdrawalBetWinner',
        'fund1Win', 'withdrawal1Win',
        'fundMelbet', 'withdrawalMelbet',
        'fundEightStarz', 'withdrawalEightStarz',
        'buyUsdt', 'sellUsdt'
      ];
      if (!forms.serviceAvailability[code]) {
        forms.serviceAvailability[code] = {};
      }
      opKeys.forEach(key => {
        forms.serviceAvailability[code][key] =
          availability?.[code]?.[key] !== undefined ? availability[code][key] : true;
      });

      // Initialize specific tutorials for this country
      forms.specificTutorials[code] = specificTutorials?.[code]
        ? JSON.parse(JSON.stringify(specificTutorials[code]))
        : {};
    });

    if (!selectedCountry.value && countryCodes.value.length > 0) {
      selectedCountry.value = countryCodes.value[0];
    }
  },
  { immediate: true, deep: true }
);

// CHANGE TRACKING
const hasChanges = computed(() => {
  return countryCodes.value.some((code) => {
    const contactsChanged = Object.keys(forms.contacts[code] || {}).some(
      key => forms.contacts[code][key] !== contactsSource.value?.[code]?.[key]
    );
    const addressesChanged = Object.keys(forms.addresses[code] || {}).some(
      key => forms.addresses[code][key] !== addressesSource.value?.[code]?.[key]
    );
    const availabilityChanged = Object.keys(forms.serviceAvailability[code] || {}).some(
      key => forms.serviceAvailability[code][key] !== serviceAvailabilitySource.value?.[code]?.[key]
    );
    const tutorialsChanged = JSON.stringify(forms.specificTutorials[code] || {}) !== JSON.stringify(specificTutorialsSource.value?.[code] || {});
    return contactsChanged || addressesChanged || availabilityChanged || tutorialsChanged;
  });
});

const selectedCountryHasChanges = computed(() => {
  const code = selectedCountry.value;
  if (!code) return false;
  const contactsChanged = Object.keys(forms.contacts[code] || {}).some(
    key => forms.contacts[code][key] !== contactsSource.value?.[code]?.[key]
  );
  const addressesChanged = Object.keys(forms.addresses[code] || {}).some(
    key => forms.addresses[code][key] !== addressesSource.value?.[code]?.[key]
  );
  const availabilityChanged = Object.keys(forms.serviceAvailability[code] || {}).some(
    key => forms.serviceAvailability[code][key] !== serviceAvailabilitySource.value?.[code]?.[key]
  );
  const tutorialsChanged = JSON.stringify(forms.specificTutorials[code] || {}) !== JSON.stringify(specificTutorialsSource.value?.[code] || {});
  return contactsChanged || addressesChanged || availabilityChanged || tutorialsChanged;
});

const selectedCountryData = computed(() => {
  const country = Countries.find(selectedCountry.value);
  return {
    name: country?.countryName || selectedCountry.value,
    code: selectedCountry.value,
    flag: country?.flag || ''
  };
});

// ACTIONS
function save() {
  if (!hasChanges.value) return;

  const combinedPayload = {
    contacts: {},
    addresses: {},
    serviceAvailability: {},
    specificTutorials: {}
  };

  let specificTutorialsChanged = false;

  countryCodes.value.forEach((code) => {
    // Contacts
    let countryContactsChanged = false;
    const countryContacts = {};
    Object.keys(forms.contacts[code] || {}).forEach((key) => {
      if (forms.contacts[code][key] !== contactsSource.value?.[code]?.[key]) {
        countryContacts[key] = forms.contacts[code][key];
        countryContactsChanged = true;
      }
    });
    if (countryContactsChanged) combinedPayload.contacts[code] = countryContacts;

    // Addresses
    let countryAddressesChanged = false;
    const countryAddresses = {};
    Object.keys(forms.addresses[code] || {}).forEach((key) => {
      if (forms.addresses[code][key] !== addressesSource.value?.[code]?.[key]) {
        countryAddresses[key] = forms.addresses[code][key];
        countryAddressesChanged = true;
      }
    });
    if (countryAddressesChanged) combinedPayload.addresses[code] = countryAddresses;

    // Service Availability
    let countryAvailabilityChanged = false;
    const countryAvailability = {};
    Object.keys(forms.serviceAvailability[code] || {}).forEach((key) => {
      if (forms.serviceAvailability[code][key] !== serviceAvailabilitySource.value?.[code]?.[key]) {
        countryAvailability[key] = forms.serviceAvailability[code][key];
        countryAvailabilityChanged = true;
      }
    });
    if (countryAvailabilityChanged) combinedPayload.serviceAvailability[code] = countryAvailability;

    // Specific Tutorials
    if (JSON.stringify(forms.specificTutorials[code] || {}) !== JSON.stringify(specificTutorialsSource.value?.[code] || {})) {
      combinedPayload.specificTutorials[code] = forms.specificTutorials[code];
      specificTutorialsChanged = true;
    }
  });

  // Envoyer une seule requête de mise à jour avec toutes les données regroupées
  $bus.emit($constants.EVENTS.APP_DATA.UPDATE, combinedPayload);

  // If specific tutorials changed, they belong to "general" section in backend
  if (specificTutorialsChanged) {
    const fullTutorials = {};
    // Get current tutorials from source to preserve other countries
    Object.assign(fullTutorials, specificTutorialsSource.value);
    // Apply changes from all countries in form
    countryCodes.value.forEach(code => {
      fullTutorials[code] = forms.specificTutorials[code];
    });

    $bus.emit($constants.EVENTS.APP_DATA.UPDATE_SPECIFIC_TUTORIALS, {
      general: {
        specificTutorials: fullTutorials
      }
    });
  }
}

function setTutorialLink(country, serviceId, key, value) {
  if (!forms.specificTutorials[country]) forms.specificTutorials[country] = {};
  if (!forms.specificTutorials[country][serviceId]) forms.specificTutorials[country][serviceId] = {};
  forms.specificTutorials[country][serviceId][key] = value;
}

function resetAll() {
  countryCodes.value.forEach((code) => {
    if (addressesSource.value?.[code]) Object.assign(forms.addresses[code], addressesSource.value[code]);
    if (serviceAvailabilitySource.value?.[code]) Object.assign(forms.serviceAvailability[code], serviceAvailabilitySource.value[code]);
    if (specificTutorialsSource.value?.[code]) forms.specificTutorials[code] = JSON.parse(JSON.stringify(specificTutorialsSource.value[code]));
  });
}

function resetCountry(code) {
  if (contactsSource.value?.[code]) Object.assign(forms.contacts[code], contactsSource.value[code]);
  if (addressesSource.value?.[code]) Object.assign(forms.addresses[code], addressesSource.value[code]);
  if (serviceAvailabilitySource.value?.[code]) Object.assign(forms.serviceAvailability[code], serviceAvailabilitySource.value[code]);
  if (specificTutorialsSource.value?.[code]) forms.specificTutorials[code] = JSON.parse(JSON.stringify(specificTutorialsSource.value[code]));
}
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center sticky top-0 py-2 z-10 bg-white/80 backdrop-blur-sm">
      <h2 class="text-xl font-semibold">Configuration par pays</h2>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700 font-medium transition-all hover:bg-gray-400"
          @click="resetAll"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white font-medium shadow-lg shadow-primary-200 transition-all hover:bg-primary-700 active:scale-95"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- COUNTRY SELECTOR -->
    <div class="flex flex-wrap gap-2">
      <button
        v-for="code in countryCodes"
        :key="code"
        @click="selectedCountry = code"
        :class="[
          'px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2',
          selectedCountry === code
            ? 'bg-primary text-white shadow-lg'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        ]"
      >
        <span class="text-lg">{{ Countries.find(code)?.flag }}</span>
        <span>{{ Countries.find(code)?.countryName || code }}</span>
      </button>
    </div>

    <!-- DETAILS -->
    <div v-if="selectedCountry" class="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      <!-- COUNTRY HEADER -->
      <div class="flex justify-between items-center pb-4 border-b border-gray-200">
        <div class="flex items-center gap-3">
          <span class="text-3xl">{{ selectedCountryData.flag }}</span>
          <div>
            <h3 class="text-2xl font-bold text-gray-800">{{ selectedCountryData.name }}</h3>
            <span class="text-sm font-mono text-gray-500 uppercase tracking-widest">{{ selectedCountryData.code }}</span>
          </div>
        </div>
        <button
          v-if="selectedCountryHasChanges"
          class="px-4 py-2 rounded-xl bg-amber-50 text-amber-600 font-medium border border-amber-200 hover:bg-amber-100 transition-all"
          @click="resetCountry(selectedCountry)"
        >
          Annuler les modifs
        </button>
      </div>

      <div class="flex flex-col gap-8">
        
        <!-- CONTACTS SECTION -->
        <section class="space-y-6">
          <div class="flex items-center gap-2 text-primary-600">
            <span class="text-xl">📞</span>
            <h4 class="text-lg font-bold">Contacts</h4>
          </div>
          
          <div class="bg-slate-50 rounded-3xl p-6 border border-slate-200 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-1">
                <label class="text-xs font-bold text-gray-500 uppercase ml-1">WhatsApp Principal</label>
                <input class="inputComponent" v-model="forms.contacts[selectedCountry].whatsappPhoneNumber" placeholder="+..." />
              </div>
              <div class="space-y-1">
                <label class="text-xs font-bold text-gray-500 uppercase ml-1">WhatsApp Réclamations</label>
                <input class="inputComponent" v-model="forms.contacts[selectedCountry].whatsappPhoneNumberComplaint" placeholder="+..." />
              </div>
            </div>
            
            <div class="space-y-1">
              <label class="text-xs font-bold text-gray-500 uppercase ml-1">Lien WhatsApp Direct</label>
              <input class="inputComponent" v-model="forms.contacts[selectedCountry].whatsappLink" placeholder="https://wa.me/..." />
            </div>
            
            <div class="space-y-1">
              <label class="text-xs font-bold text-gray-500 uppercase ml-1">Lien WhatsApp Chat</label>
              <input class="inputComponent" v-model="forms.contacts[selectedCountry].whatsappChatLink" placeholder="Lien vers groupe ou chat..." />
            </div>
            
            <div class="space-y-1">
              <label class="text-xs font-bold text-gray-500 uppercase ml-1">Lien Telegram</label>
              <input class="inputComponent" v-model="forms.contacts[selectedCountry].telegramLink" placeholder="https://t.me/..." />
            </div>
          </div>
        </section>

        <!-- ADDRESSES SECTION -->
        <section class="space-y-6">
          <div class="flex items-center gap-2 text-primary-600">
            <span class="text-xl">📍</span>
            <h4 class="text-lg font-bold">Adresses & Retraits</h4>
          </div>

          <div class="bg-slate-50 rounded-3xl p-6 border border-slate-200 space-y-6">
            <!-- CRYPTO -->
            <div class="space-y-3">
              <span class="text-sm font-bold text-gray-400 uppercase tracking-tighter">Portefeuilles USDT</span>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-gray-500 uppercase ml-1">TRC20</label>
                  <input class="inputComponent !text-xs" v-model="forms.addresses[selectedCountry].usdtTrc20Address" />
                </div>
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-gray-500 uppercase ml-1">ERC20</label>
                  <input class="inputComponent !text-xs" v-model="forms.addresses[selectedCountry].usdtErc20Address" />
                </div>
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-gray-500 uppercase ml-1">BEP20</label>
                  <input class="inputComponent !text-xs" v-model="forms.addresses[selectedCountry].usdtBep20Address" />
                </div>
              </div>
            </div>

            <!-- BINANCE -->
            <div class="space-y-1">
              <label class="text-xs font-bold text-gray-500 uppercase ml-1">Binance ID</label>
              <input class="inputComponent" v-model="forms.addresses[selectedCountry].binanceAccountId" />
            </div>

            <!-- BETTING -->
            <div class="space-y-4 pt-2">
              <span class="text-sm font-bold text-gray-400 uppercase tracking-tighter">Comptes Betting (Retrait)</span>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="space-y-1">
                  <label class="text-xs font-bold text-gray-500 uppercase ml-1">1xBet</label>
                  <textarea class="inputComponent h-16 resize-none text-xs" v-model="forms.addresses[selectedCountry].xbetWithdrawAddress" placeholder="Un compte par ligne..."></textarea>
                </div>
                <div class="space-y-1">
                  <label class="text-xs font-bold text-gray-500 uppercase ml-1">Betwinner</label>
                  <textarea class="inputComponent h-16 resize-none text-xs" v-model="forms.addresses[selectedCountry].betwinnerWithdrawAddress" placeholder="Un compte par ligne..."></textarea>
                </div>
                <div class="space-y-1">
                  <label class="text-xs font-bold text-gray-500 uppercase ml-1">Melbet</label>
                  <textarea class="inputComponent h-16 resize-none text-xs" v-model="forms.addresses[selectedCountry].melbetWithdrawAddress" placeholder="Un compte par ligne..."></textarea>
                </div>
                <div class="space-y-1">
                  <label class="text-xs font-bold text-gray-500 uppercase ml-1">8 Starz</label>
                  <textarea class="inputComponent h-16 resize-none text-xs" v-model="forms.addresses[selectedCountry].eightStarzWithdrawAddress" placeholder="Un compte par ligne..."></textarea>
                </div>
                <div class="space-y-1 md:col-span-2">
                  <label class="text-xs font-bold text-gray-500 uppercase ml-1">1Win</label>
                  <textarea class="inputComponent h-16 resize-none text-xs" v-model="forms.addresses[selectedCountry].oneWinWithdrawAddress" placeholder="Un compte par ligne..."></textarea>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      <!-- SERVICE AVAILABILITY SECTION -->
      <section class="space-y-6 pt-4">
        <div class="flex items-center gap-2 text-primary-600">
          <span class="text-xl">🚦</span>
          <h4 class="text-lg font-bold">Disponibilité des services</h4>
        </div>

        <div class="bg-slate-50 rounded-3xl p-6 border border-slate-200 overflow-hidden">
          <table class="w-full text-left">
            <thead>
              <tr class="text-xs font-bold text-gray-400 uppercase tracking-tighter border-b border-slate-200">
                <th class="pb-3 px-2">Service</th>
                <th class="pb-3 px-2 text-center">Recharge / Achat</th>
                <th class="pb-3 px-2 text-center">Retrait / Vente</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr v-for="service in [
                { id: '1xBet', label: '1xBet', fund: 'fund1xBet', withdraw: 'withdrawal1xBet' },
                { id: 'BetWinner', label: 'BetWinner', fund: 'fundBetWinner', withdraw: 'withdrawalBetWinner' },
                { id: '1Win', label: '1Win', fund: 'fund1Win', withdraw: 'withdrawal1Win' },
                { id: 'Melbet', label: 'Melbet', fund: 'fundMelbet', withdraw: 'withdrawalMelbet' },
                { id: 'EightStarz', label: '8Starz', fund: 'fundEightStarz', withdraw: 'withdrawalEightStarz' },
                { id: 'USDT', label: 'USDT (Crypto)', fund: 'buyUsdt', withdraw: 'sellUsdt' }
              ]" :key="service.id" class="hover:bg-slate-100/50 transition-colors">
                <td class="py-4 px-2 font-medium text-gray-700">{{ service.label }}</td>
                <td class="py-4 px-2 text-center">
                  <label class="relative inline-flex items-center cursor-pointer justify-center">
                    <input type="checkbox" v-model="forms.serviceAvailability[selectedCountry][service.fund]" class="sr-only peer">
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </td>
                <td class="py-4 px-2 text-center">
                  <label class="relative inline-flex items-center cursor-pointer justify-center">
                    <input type="checkbox" v-model="forms.serviceAvailability[selectedCountry][service.withdraw]" class="sr-only peer">
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- TUTORIELS VIDÉO SECTION -->
      <section class="space-y-6 pt-4">
        <div class="flex items-center gap-2 text-primary-600">
          <span class="text-xl">🎬</span>
          <h4 class="text-lg font-bold">Tutoriels vidéo</h4>
        </div>

        <div class="bg-slate-50 rounded-3xl p-6 border border-slate-200 space-y-6">
          <div v-for="service in TRANSACTIONS.SERVICES_LIST" :key="service.id" class="space-y-3 border-b border-slate-200 pb-6 last:border-0 last:pb-0">
            <div class="flex items-center gap-2">
              <ImgComponent :src="service.logo" class="w-6 h-6 object-contain" />
              <span class="font-semibold text-gray-700">{{ service.name }}</span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-1">
                <label class="text-[10px] font-bold text-gray-400 uppercase ml-1">Vidéo Recharge / Achat</label>
                <input 
                  class="inputComponent !text-xs" 
                  placeholder="https://youtube.com/..."
                  :value="forms.specificTutorials[selectedCountry]?.[service.id]?.rechargeVideoLink || ''"
                  @input="e => setTutorialLink(selectedCountry, service.id, 'rechargeVideoLink', e.target.value)"
                />
              </div>
              <div class="space-y-1">
                <label class="text-[10px] font-bold text-gray-400 uppercase ml-1">Vidéo Retrait / Vente</label>
                <input 
                  class="inputComponent !text-xs" 
                  placeholder="https://youtube.com/..."
                  :value="forms.specificTutorials[selectedCountry]?.[service.id]?.withdrawVideoLink || ''"
                  @input="e => setTutorialLink(selectedCountry, service.id, 'withdrawVideoLink', e.target.value)"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
